import React, { useRef, useEffect, useState } from "react";
import {
  useParams,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import QuestionRenderer from "../components/QuestionRenderer";
import Header from "./Header";
import Footer from "./Footer";
import {
  EVENT_NAMES,
  PARAM_KEYS,
  setPersistentContext,
  trackEvent,
} from "../analytics";
import { getApiBaseUrl } from "../services/apiBase";
import { detectRegion } from "../services/locationApi";

const API_BASE_URL = getApiBaseUrl();

// --------------------------------------------------
// 🔥 FEATURED CTA COMPONENT
// --------------------------------------------------
const FeaturedCTA = React.memo(function FeaturedCTA({ featured, categoryKey }) {
    if (!featured?.is_featured) return null;

    const c = featured.contractor;
    const contractorName = c.company_name || c.provider_name || "Featured contractor";
    const contractorTagline =
      c.tagline ||
      `Top-rated ${featured.category_name || "home service"} contractor near you.`;
    const providerPath = `/providers/${c.provider_slug}`;

    function openFeaturedProfile(event) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }

      trackEvent(EVENT_NAMES.FEATURED_CONTRACTOR_CLICK, {
        [PARAM_KEYS.PROVIDER_ID]: c.provider_slug || c.provider_guid,
        [PARAM_KEYS.CATEGORY_KEY]: categoryKey,
        [PARAM_KEYS.PLACEMENT]: "survey_featured_contractor",
      });

      const nextWindow = window.open(providerPath, "_blank", "noopener,noreferrer");
      if (nextWindow) {
        nextWindow.opener = null;
      }
    }

    return (
      <aside
        className="featured-cta"
        onClick={openFeaturedProfile}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            openFeaturedProfile(event);
          }
        }}
      >
        <div className="featured-cta-badge">Recommended pro</div>
        <div className="featured-cta-media">
          <img src={c.image_url} alt={contractorName} />
        </div>
        <div className="featured-cta-body">
          <p className="featured-cta-eyebrow">Featured contractor</p>
          <h3>{contractorName}</h3>
          <p className="featured-cta-copy">{contractorTagline}</p>
          <a
            className="featured-cta-link"
            href={providerPath}
            target="_blank"
            rel="noopener noreferrer"
            onClick={openFeaturedProfile}
          >
            View contractor profile
          </a>
          <p className="featured-cta-disclaimer">(sponsored)</p>
        </div>
      </aside>
    );
  });

function getStepTitle(uiStep) {
  if (uiStep === "lead") return "Almost there";
  if (uiStep === "upload") return "Add photos if you have them";
  return null;
}

function getStepDescription(uiStep) {
  if (uiStep === "lead") {
    return "Enter your contact details to view your free project summary.";
  }

  if (uiStep === "upload") {
    return "Photos are optional, but they can help us tailor your project summary.";
  }

  return null;
}

function getPrimaryButtonLabel(uiStep) {
  if (uiStep === "lead") return "Get My Free Project Summary";
  if (uiStep === "upload") return "Skip Photo Upload";
  return "Next";
}

function getTrustMessage(uiStep) {
  if (uiStep === "lead") {
    return "Free project summary. No obligation. Your information is kept private.";
  }

  if (uiStep === "upload") {
    return "Optional step. You can continue without adding photos.";
  }

  return null;
}

const CAMPAIGN_STORAGE_KEY = "homehelper_campaign_id";
const CONSUMER_LEAD_CONSENT_VERSION = "2026-07-27-consumer-marketing-consent-v1";
const REGION_PROXIMITY_STORAGE_KEY = "homehelper_region_proximity";
const REGION_SLUG_STORAGE_KEY = "homehelper_region_slug";
const REGION_NAME_STORAGE_KEY = "homehelper_region_name";
const ZIP_REGEX = /^\d{5}$/;

function buildRegionProximityQuestion(regionName) {
  const normalizedRegionName = String(regionName || "").trim();

  if (!normalizedRegionName) {
    return null;
  }

  if (/region$/i.test(normalizedRegionName)) {
    return `Do you live within 30 minutes of the ${normalizedRegionName}?`;
  }

  return `Do you live within 30 minutes of the ${normalizedRegionName} region?`;
}

function getStoredCampaignId() {
  try {
    const raw = localStorage.getItem(CAMPAIGN_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed?.value || !parsed?.expiresAt || Date.now() > parsed.expiresAt) {
      localStorage.removeItem(CAMPAIGN_STORAGE_KEY);
      return null;
    }

    return parsed.value;
  } catch {
    localStorage.removeItem(CAMPAIGN_STORAGE_KEY);
    return null;
  }
}

function getStoredZip() {
  try {
    return localStorage.getItem("hh_zip") || localStorage.getItem("user_zip") || "";
  } catch {
    return "";
  }
}

function normalizeZip(value) {
  const zip = String(value || "").replace(/\D/g, "").slice(0, 5);
  return ZIP_REGEX.test(zip) ? zip : "";
}

function getStoredRegionContext() {
  try {
    const raw = localStorage.getItem(REGION_PROXIMITY_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;

    return {
      proximityAnswer: parsed?.answer || null,
      livesWithinRegionDriveTime:
        typeof parsed?.lives_within_region_drive_time === "boolean"
          ? parsed.lives_within_region_drive_time
          : null,
      regionSlug:
        parsed?.region_slug ||
        localStorage.getItem(REGION_SLUG_STORAGE_KEY) ||
        "",
      regionName:
        parsed?.region_name_snapshot ||
        localStorage.getItem(REGION_NAME_STORAGE_KEY) ||
        "",
      regionProximityQuestion: parsed?.region_proximity_question || null,
    };
  } catch {
    return {
      proximityAnswer: null,
      livesWithinRegionDriveTime: null,
      regionSlug: "",
      regionName: "",
      regionProximityQuestion: null,
    };
  }
}

function normalizeAnalyticsQuestionType(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/-/g, "_");
}

function buildSurveyStepMeta(uiStep, step) {
  if (uiStep === "survey") {
    return {
      [PARAM_KEYS.STEP_INDEX]: step + 1,
      [PARAM_KEYS.STEP_NAME]: `survey_question_${step + 1}`,
    };
  }

  if (uiStep === "upload") {
    return {
      [PARAM_KEYS.STEP_INDEX]: step + 2,
      [PARAM_KEYS.STEP_NAME]: "upload_photos",
    };
  }

  if (uiStep === "lead") {
    return {
      [PARAM_KEYS.STEP_INDEX]: step + 3,
      [PARAM_KEYS.STEP_NAME]: "lead_form",
    };
  }

  return {};
}

function buildSafeAnswerValue(questionType, nextAnswer) {
  if (questionType === "single_select") {
    return typeof nextAnswer === "string" ? nextAnswer.trim().slice(0, 80) : undefined;
  }

  if (questionType === "multi_select") {
    return Array.isArray(nextAnswer)
      ? nextAnswer
          .map((entry) => String(entry || "").trim())
          .filter(Boolean)
          .slice(0, 5)
          .join("|")
      : undefined;
  }

  if (questionType === "text") {
    return typeof nextAnswer === "string" && nextAnswer.trim() ? "text_entered" : undefined;
  }

  return undefined;
}

function getPhotoCount(value) {
  return Array.isArray(value?.files) ? value.files.length : 0;
}

const CATEGORY_SLUG_ALIASES = {
  "arborist-tree-services": "landscaping",
  concrete: "concrete-foundation",
  "exterior-door-replacement": "windows-replacement",
  "fence-install": "fence-gates",
  "foundation-repair": "concrete-foundation",
  gates: "fence-gates",
  "pool-maintenance": "pools",
};

function normalizeCategorySlug(slug) {
  return CATEGORY_SLUG_ALIASES[slug] || slug;
}

export default function SurveyPage() {
  const params = useParams();
  const rawSlug = params.slug;
  const slug = normalizeCategorySlug(rawSlug);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const bootstrappingRef = useRef(false);
  const surveyCapturedAtRef = useRef(new Date().toISOString());

  const step = parseInt(searchParams.get("step") || "0", 10);
  const storedRegionContext = getStoredRegionContext();
  const zip = location.state?.zip || getStoredZip();
  const regionSlug =
    location.state?.regionSlug || storedRegionContext.regionSlug || "";
  const regionName =
    location.state?.regionName || storedRegionContext.regionName || "";
  const proximityAnswer =
    location.state?.proximityAnswer || storedRegionContext.proximityAnswer || null;
  const livesWithinRegionDriveTime =
    typeof location.state?.livesWithinRegionDriveTime === "boolean"
      ? location.state.livesWithinRegionDriveTime
      : storedRegionContext.livesWithinRegionDriveTime;
  const regionProximityQuestion =
    location.state?.regionProximityQuestion ||
    storedRegionContext.regionProximityQuestion ||
    buildRegionProximityQuestion(regionName);

  const [surveyId, setSurveyId] = useState(null);
  const [customerId, setCustomerId] = useState(
    sessionStorage.getItem("surveyCustomerId")
  );
  const [question, setQuestion] = useState(null);
  const [answer, setAnswer] = useState(null);

  const [uiStep, setUiStep] = useState("survey"); // survey | upload | lead
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [validationError, setValidationError] = useState("");
  const [locationValidated, setLocationValidated] = useState(false);
  const [renderScreen, setRenderScreen] = useState(null);
  const [financing, setFinancing] = useState(null);
  const hasSentFinancingRef = useRef(false);
  const surveyStartTrackedRef = useRef(false);
  const viewedStepSignaturesRef = useRef(new Set());
  const latestSurveyPayloadRef = useRef({});
  const surveyCompletedRef = useRef(false);
  const surveyAbandonTrackedRef = useRef(false);
  const userAnsweredRef = useRef(false);

  useEffect(() => {
    if (!rawSlug || rawSlug === slug) return;

    navigate(`/survey/${slug}${location.search || ""}`, {
      replace: true,
      state: location.state,
    });
  }, [location.search, location.state, navigate, rawSlug, slug]);

  useEffect(() => {
    let cancelled = false;

    async function validateEntryZip() {
      const normalizedZip = normalizeZip(zip);

      if (!normalizedZip) {
        redirectToSurveyEntry("");
        return;
      }

      setLoading(true);
      setError("");

      const result = await validateZipAgainstRegion(normalizedZip);
      if (cancelled) {
        return;
      }

      if (!result.ok) {
        redirectToSurveyEntry(result.zip);
        return;
      }

      setLocationValidated(true);
      setLoading(false);
    }

    validateEntryZip();

    return () => {
      cancelled = true;
    };
  }, [slug, zip]);

  // 🔥 NEW: Featured contractor state
    const [featured, setFeatured] = useState(() => {
    try {
      const cached = sessionStorage.getItem("featured");
      return cached ? JSON.parse(cached) : null;
    } catch (e) {
      return null;
    }
  });

  // 🔥 SAFETY: ensure validation exists in scope
  function safeValidateAnswer(screen, answer) {
    if (!screen) return "Please complete the form.";

    switch (screen.type) {
      case "text":
        if (screen.required === false) return "";
        return answer && answer.trim() ? "" : "This field is required.";
      case "single-select":
        return answer ? "" : "Please select an option.";
      case "multi-select":
        return Array.isArray(answer) && answer.length > 0
          ? ""
          : "Please select at least one option.";
      case "text":
        return answer && answer.trim() ? "" : "This field is required.";
      default:
        return "";
    }
  }

  function safeValidateLeadForm(values = {}) {
    const requiredFields = [
      "name",
      "email",
      "street",
      "city",
      "state",
      "zip",
    ];

    for (const field of requiredFields) {
      if (!values[field] || !values[field].toString().trim()) {
        return "Please complete all required fields.";
      }
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(values.email)) {
      return "Please enter a valid email address.";
    }

    const phoneDigits = (values.phone || "").replace(/\D/g, "");
    if (phoneDigits && phoneDigits.length < 10) {
      return "Please enter a valid phone number or leave it blank.";
    }

    const zipRegex = /^\d{5}$/;
    if (!zipRegex.test(values.zip)) {
      return "Please enter a valid 5-digit ZIP code.";
    }

    if (values.marketingConsentAccepted !== true) {
      return "Please agree to the Terms & Conditions and Privacy Policy to continue.";
    }

    return "";
  }

  async function validateZipAgainstRegion(zipValue) {
    const normalizedZip = normalizeZip(zipValue);

    if (!normalizedZip) {
      return {
        ok: false,
        zip: "",
        message: "Please enter a valid 5-digit ZIP code.",
      };
    }

    try {
      const payload = await detectRegion({
        categorySlug: slug,
        zip: normalizedZip,
      });

      if (!payload?.region?.id) {
        return {
          ok: false,
          zip: normalizedZip,
          message:
            "That ZIP code does not match an available Home Helper service region.",
        };
      }

      return {
        ok: true,
        zip: normalizedZip,
        payload,
      };
    } catch (_error) {
      return {
        ok: false,
        zip: normalizedZip,
        message: "We couldn't verify that ZIP code right now. Please try again.",
      };
    }
  }

  function redirectToSurveyEntry(zipValue = "") {
    const normalizedZip = normalizeZip(zipValue);
    navigate(normalizedZip ? `/${normalizedZip}/${slug}` : `/in-town/${slug}`, {
      replace: true,
    });
  }

  function safeMapApiType(type) {
    if (type === "radio") return "single-select";
    if (type === "checkbox") return "multi-select";
    return type;
  }

  const featuredRef = useRef(featured);

  function buildSurveyEventPayload(overrides = {}) {
    const questionType = normalizeAnalyticsQuestionType(
      overrides.questionType || question?.type || renderScreen?.type
    );

    return {
      [PARAM_KEYS.SURVEY_ID]: surveyId,
      [PARAM_KEYS.CATEGORY_KEY]: slug,
      [PARAM_KEYS.ZIP_CODE]: zip,
      [PARAM_KEYS.QUESTION_ID]:
        overrides.questionId !== undefined ? overrides.questionId : question?.questionId,
      [PARAM_KEYS.QUESTION_TYPE]: questionType || undefined,
      ...buildSurveyStepMeta(overrides.uiStep || uiStep, step),
      ...overrides,
    };
  }

  useEffect(() => {
    if (featured) {
      featuredRef.current = featured;
    }
  }, [featured]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem("financing_data");
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") {
        setFinancing(parsed);
      }
    } catch (err) {
      // ignore malformed storage
    }
  }, []);


  // --------------------------------------------------
  // 🔥 FETCH FEATURED CONTRACTOR (ONCE)
  // --------------------------------------------------
  useEffect(() => {
    async function fetchFeatured() {
      if (!zip || !slug) return;

      try {
        const res = await fetch(
          `${API_BASE_URL}/api/featured-contractor?zip=${zip}&category=${slug}`
        );
        const data = await res.json();

        // ✅ Store in state
        setFeatured(data);

        // ✅ Persist to sessionStorage (prevents flashing on re-render)
        try {
          sessionStorage.setItem("featured", JSON.stringify(data));
        } catch (e) {
          console.warn("Failed to cache featured contractor", e);
        }
      } catch (err) {
        console.error("Featured contractor fetch failed:", err);
      }
    }

    fetchFeatured();
  }, [zip, slug]);

  // --------------------------------------------------
  // BOOTSTRAP CUSTOMER (ONCE)
  // --------------------------------------------------
  useEffect(() => {
  async function bootstrapCustomer() {
    if (!locationValidated) return;
    if (bootstrappingRef.current) return;
    bootstrappingRef.current = true;

    try {
      sessionStorage.removeItem("surveyCustomerId");

      const campaignId = getStoredCampaignId();

      if (campaignId) {
        setPersistentContext({
          [PARAM_KEYS.CAMPAIGN_ID]: campaignId,
        });
      }

      const body = {
        zip,
        zipcode: zip || null,
        campaign_id: campaignId,
        category_slug: slug,
        region_slug: regionSlug || null,
        region_name_snapshot: regionName || null,
        region_proximity_answer: proximityAnswer || null,
        lives_within_region_drive_time:
          typeof livesWithinRegionDriveTime === "boolean"
            ? livesWithinRegionDriveTime
            : null,
        region_proximity_question: regionProximityQuestion || null,
        source: "survey",
        capturedAt: surveyCapturedAtRef.current,
      };

      const res = await fetch(`${API_BASE_URL}/api/customers/bootstrap`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const details = await res.json().catch(() => null);
        const error = new Error(details?.error || "Customer bootstrap failed");
        error.details = details;
        throw error;
      }

      const data = await res.json();

      setCustomerId(data.customerId);
      sessionStorage.setItem("surveyCustomerId", data.customerId);
      hasSentFinancingRef.current = true;

    } catch (err) {
      console.error(err);
      if (
        err?.details?.code === "ZIP_REGION_UNRESOLVED" ||
        err?.details?.code === "ZIP_OUTSIDE_SANTA_CLARITA" ||
        err?.details?.code === "INVALID_ZIP"
      ) {
        redirectToSurveyEntry(zip);
        return;
      }
      setError("Unable to start survey. Please refresh.");
    } finally {
      setLoading(false);
    }
  }

  bootstrapCustomer();
}, [
  locationValidated,
  zip,
  financing,
  slug,
  regionSlug,
  regionName,
  proximityAnswer,
  livesWithinRegionDriveTime,
  regionProximityQuestion,
]);

  // --------------------------------------------------
  // LOAD QUESTION
  // --------------------------------------------------
  useEffect(() => {
    if (!customerId) return;

    let cancelled = false;

    async function load() {
      try {
        setLoading(true);
        setError("");
        setValidationError("");
        setAnswer(null);

        const res = await fetch(
          `${API_BASE_URL}/api/surveys/${slug}/question?step=${step}&customerId=${customerId}`
        );

        if (!res.ok) throw new Error("Failed to load");

        const data = await res.json();
        if (cancelled) return;

        setSurveyId(data.surveyId);
        setQuestion(data.question);
        setUiStep(data.isLastStep ? "upload" : "survey");

        if (!surveyStartTrackedRef.current && data.surveyId) {
          surveyStartTrackedRef.current = true;
          trackEvent(EVENT_NAMES.SURVEY_START, {
            [PARAM_KEYS.SURVEY_ID]: data.surveyId,
            [PARAM_KEYS.CATEGORY_KEY]: slug,
            [PARAM_KEYS.ZIP_CODE]: zip,
            ...buildSurveyStepMeta("survey", step),
          });
        }

        let builtScreen = null;

        if (!data.isLastStep && data.question) {
          builtScreen = {
            id: data.question.questionId,
            type: safeMapApiType(data.question.type),
            question: data.question.text,
            options: data.question.options || [],
            helpText: data.question.helpText,
            required: data.question.required,
          };
        }

        if (data.isLastStep) {
          builtScreen = {
            id: "__upload__",
            type: "file_upload",
            question: "Do you want to upload a photo?",
            helpText:
              "This step is optional. You may upload up to three photos or just click 'Skip'  to get your results.",
          };
        }

        setRenderScreen(builtScreen);
        setAnswer(data.isLastStep ? null : data.answerValue ?? null);
        setSubmitting(false);
      } catch (err) {
        console.error(err);
        if (!cancelled) setError("Unable to load survey step.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [slug, step, customerId]);

  useEffect(() => {
    if (!renderScreen || loading || !surveyId) {
      return;
    }

    const questionType = normalizeAnalyticsQuestionType(question?.type || renderScreen.type);
    const stepSignature = `${surveyId}:${uiStep}:${step}:${question?.questionId || renderScreen.id}`;

    if (viewedStepSignaturesRef.current.has(stepSignature)) {
      return;
    }

    viewedStepSignaturesRef.current.add(stepSignature);

    trackEvent(EVENT_NAMES.VIEW_SURVEY_STEP, {
      [PARAM_KEYS.SURVEY_ID]: surveyId,
      [PARAM_KEYS.CATEGORY_KEY]: slug,
      [PARAM_KEYS.ZIP_CODE]: zip,
      [PARAM_KEYS.QUESTION_ID]: question?.questionId,
      [PARAM_KEYS.QUESTION_TYPE]: questionType,
      ...buildSurveyStepMeta(uiStep, step),
    });
  }, [loading, question, renderScreen, slug, step, surveyId, uiStep, zip]);

  useEffect(() => {
    latestSurveyPayloadRef.current = buildSurveyEventPayload({
      [PARAM_KEYS.HAS_PHOTOS]: getPhotoCount(answer) > 0,
      [PARAM_KEYS.PHOTO_COUNT]: getPhotoCount(answer) || undefined,
    });
  }, [answer, question, renderScreen, slug, step, surveyId, uiStep, zip]);

  useEffect(() => {
    function trackSurveyAbandon() {
      if (
        !surveyStartTrackedRef.current ||
        surveyCompletedRef.current ||
        surveyAbandonTrackedRef.current
      ) {
        return;
      }

      surveyAbandonTrackedRef.current = true;

      trackEvent(EVENT_NAMES.SURVEY_ABANDON, latestSurveyPayloadRef.current);
    }

    window.addEventListener("pagehide", trackSurveyAbandon);

    return () => {
      window.removeEventListener("pagehide", trackSurveyAbandon);
      trackSurveyAbandon();
    };
  }, []);



  // --------------------------------------------------
  // AUTO-ADVANCE AFTER SUCCESSFUL UPLOAD
  // --------------------------------------------------
  useEffect(() => {
    if (
      uiStep === "upload" &&
      answer?.type === "file_upload" &&
      answer.uploadComplete
    ) {
      goToLeadForm();
    }
  }, [uiStep, answer]);

  function goToLeadForm() {
    setUiStep("lead");

    setRenderScreen({
      id: "__lead_form__",
      type: "lead-form",
      question: "Almost There!",
      uploadedImages: answer?.files || [],
      fields: [
        { id: "name" },
        { id: "phone", type: "phone" },
        { id: "email", type: "email" },
        { id: "street" },
        { id: "city" },
        { id: "state" },
        { id: "zip" },
      ],
    });

    setAnswer((prev) => ({
      ...(prev || {}),
      phone: answer?.phone || prev?.phone || "",
      zip: zip || prev?.zip || "",
      marketingConsentAccepted: prev?.marketingConsentAccepted === true,
      consentVersion: prev?.consentVersion || CONSUMER_LEAD_CONSENT_VERSION,
    }));
  }

  function handlePhotoUploadRequest({ fileCount }) {
    trackEvent(
      EVENT_NAMES.LEAD_PHOTO_UPLOAD,
      buildSurveyEventPayload({
        uiStep: "upload",
        questionId: renderScreen?.id,
        questionType: "file_upload",
        [PARAM_KEYS.HAS_PHOTOS]: fileCount > 0,
        [PARAM_KEYS.PHOTO_COUNT]: fileCount,
      })
    );
  }

  function handlePhotoUploadSuccess({ fileCount }) {
    trackEvent(
      EVENT_NAMES.LEAD_PHOTO_UPLOAD_SUCCESS,
      buildSurveyEventPayload({
        uiStep: "upload",
        questionId: renderScreen?.id,
        questionType: "file_upload",
        [PARAM_KEYS.HAS_PHOTOS]: fileCount > 0,
        [PARAM_KEYS.PHOTO_COUNT]: fileCount,
      })
    );
  }

  function handleAnswerChange(nextAnswer) {
    userAnsweredRef.current = uiStep === "survey" && renderScreen?.type === "single-select";
    setAnswer(nextAnswer);
  }

  // ADD THIS NEW EFFECT BELOW YOUR EXISTING useEffect BLOCKS

  // --------------------------------------------------
  // 🔥 AUTO-ADVANCE FOR RADIO (SINGLE-SELECT)
  // --------------------------------------------------
  useEffect(() => {
    if (
      uiStep === "survey" &&
      renderScreen?.type === "single-select" &&
      answer &&
      userAnsweredRef.current &&
      !loading &&
      !submitting
    ) {
      userAnsweredRef.current = false;

      // slight delay ensures UI updates before navigation
      const timer = setTimeout(() => {
        handleNext();
      }, 150);

      return () => clearTimeout(timer);
    }
  }, [answer, loading, renderScreen?.type, submitting, uiStep]);

  // --------------------------------------------------
  // SUBMIT ANSWER
  // --------------------------------------------------
  const submitSurveyAnswer = async () => {
    if (!surveyId || !question || !customerId) return null;

    try {
      setSubmitting(true);
      setLoading(true);

      const campaignId = getStoredCampaignId();

      const payload = {
        surveyId,
        customerId,
        questionId: question.questionId,
        answerValue: answer,
        campaign_id: campaignId,
        capturedAt: uiStep === "lead" ? new Date().toISOString() : surveyCapturedAtRef.current,
        entryContext: {
          region_slug: regionSlug || null,
          region_name_snapshot: regionName || null,
          answer: proximityAnswer || null,
          lives_within_region_drive_time:
            typeof livesWithinRegionDriveTime === "boolean"
              ? livesWithinRegionDriveTime
              : null,
          region_proximity_question: regionProximityQuestion || null,
        },
      };

      if (financing && !hasSentFinancingRef.current) {
        hasSentFinancingRef.current = true;
      }

      const res = await fetch(`${API_BASE_URL}/api/surveys/answer`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const details = await res.json().catch(() => null);
        const error = new Error(details?.error || "Submit failed");
        error.details = details;
        throw error;
      }

      return await res.json();
    } catch (err) {
      console.error(err);
      if (
        err?.details?.code === "ZIP_REGION_UNRESOLVED" ||
        err?.details?.code === "ZIP_OUTSIDE_SANTA_CLARITA" ||
        err?.details?.code === "INVALID_ZIP"
      ) {
        setValidationError(
          err?.details?.error ||
            "That ZIP code does not match an available Home Helper service region."
        );
      } else {
        setError("Failed to submit answer.");
      }
      return null;
    } finally {
      setLoading(false);
    }
  };

  // --------------------------------------------------
  // NEXT BUTTON HANDLER
  // --------------------------------------------------
  const handleNext = async () => {
    setError("");
    setValidationError("");

    if (uiStep === "survey") {
      const error = safeValidateAnswer(renderScreen, answer);
      if (error) {
        setValidationError(error);
        return;
      }

      const data = await submitSurveyAnswer();
      if (!data) return;

      const questionType = normalizeAnalyticsQuestionType(question?.type || renderScreen?.type);
      const stepMeta = buildSurveyStepMeta(uiStep, step);

      trackEvent(EVENT_NAMES.SURVEY_QUESTION_ANSWERED, {
        [PARAM_KEYS.SURVEY_ID]: surveyId,
        [PARAM_KEYS.CATEGORY_KEY]: slug,
        [PARAM_KEYS.QUESTION_ID]: question?.questionId,
        [PARAM_KEYS.QUESTION_TYPE]: questionType,
        [PARAM_KEYS.ANSWER_VALUE]: buildSafeAnswerValue(questionType, answer),
        ...stepMeta,
      });
      trackEvent(EVENT_NAMES.SURVEY_STEP_COMPLETED, {
        [PARAM_KEYS.SURVEY_ID]: surveyId,
        [PARAM_KEYS.CATEGORY_KEY]: slug,
        [PARAM_KEYS.QUESTION_ID]: question?.questionId,
        [PARAM_KEYS.QUESTION_TYPE]: questionType,
        ...stepMeta,
      });

      if (data.lastQuestion) {
        setUiStep("upload");
      } else if (data.nextStep !== undefined) {
        navigate(`/survey/${slug}?step=${data.nextStep}`);
      }
      return;
    }

    if (uiStep === "upload") {
      const photoCount = getPhotoCount(answer);
      const hasPhotos = photoCount > 0;

      if (!hasPhotos) {
        trackEvent(
          EVENT_NAMES.SURVEY_QUESTION_SKIPPED,
          buildSurveyEventPayload({
            uiStep: "upload",
            questionId: renderScreen?.id,
            questionType: "file_upload",
            [PARAM_KEYS.HAS_PHOTOS]: false,
            [PARAM_KEYS.PHOTO_COUNT]: 0,
          })
        );
      }

      trackEvent(EVENT_NAMES.SURVEY_STEP_COMPLETED, {
        [PARAM_KEYS.SURVEY_ID]: surveyId,
        [PARAM_KEYS.CATEGORY_KEY]: slug,
        [PARAM_KEYS.HAS_PHOTOS]: hasPhotos,
        [PARAM_KEYS.PHOTO_COUNT]: photoCount || undefined,
        ...buildSurveyStepMeta(uiStep, step),
      });
      goToLeadForm();
      return;
    }

    if (uiStep === "lead") {
      const error = safeValidateLeadForm(answer);
      if (error) {
        setValidationError(error);
        return;
      }

      const zipValidation = await validateZipAgainstRegion(answer?.zip || zip);
      if (!zipValidation.ok) {
        setValidationError(zipValidation.message);
        return;
      }

      const hasPhotos = Array.isArray(renderScreen?.uploadedImages) && renderScreen.uploadedImages.length > 0;
      const stepMeta = buildSurveyStepMeta(uiStep, step);

      trackEvent(EVENT_NAMES.LEAD_SUBMIT_ATTEMPT, {
        [PARAM_KEYS.SURVEY_ID]: surveyId,
        [PARAM_KEYS.CATEGORY_KEY]: slug,
        [PARAM_KEYS.HAS_PHOTOS]: hasPhotos,
        [PARAM_KEYS.PHOTO_COUNT]: getPhotoCount(answer) || undefined,
        ...stepMeta,
      });

      const data = await submitSurveyAnswer();
      if (!data?.customerId) {
        setError("Unable to complete survey.");
        return;
      }

      surveyCompletedRef.current = true;

      trackEvent(EVENT_NAMES.LEAD_SUBMIT_SUCCESS, {
        [PARAM_KEYS.SURVEY_ID]: surveyId,
        [PARAM_KEYS.CATEGORY_KEY]: slug,
        [PARAM_KEYS.HAS_PHOTOS]: hasPhotos,
        [PARAM_KEYS.PHOTO_COUNT]: getPhotoCount(answer) || undefined,
        ...stepMeta,
      });
      trackEvent(EVENT_NAMES.SURVEY_STEP_COMPLETED, {
        [PARAM_KEYS.SURVEY_ID]: surveyId,
        [PARAM_KEYS.CATEGORY_KEY]: slug,
        [PARAM_KEYS.HAS_PHOTOS]: hasPhotos,
        [PARAM_KEYS.PHOTO_COUNT]: getPhotoCount(answer) || undefined,
        ...stepMeta,
      });

      navigate(`/survey/${slug}/complete`, {
        state: {
          customerId,
          surveyId,
          zip,
          uploadedImages: answer?.files || [],
          regionSlug: regionSlug || null,
          regionName: regionName || null,
          proximityAnswer: proximityAnswer || null,
          livesWithinRegionDriveTime:
            typeof livesWithinRegionDriveTime === "boolean"
              ? livesWithinRegionDriveTime
              : null,
          regionProximityQuestion: regionProximityQuestion || null,
        },
      });
    }
  };

  const handleBack = () => {
    trackEvent(EVENT_NAMES.SURVEY_BACK_CLICKED, latestSurveyPayloadRef.current);

    if (uiStep === "lead") {
      setUiStep("upload");
      return;
    }

    if (uiStep === "upload") {
      navigate(`/survey/${slug}?step=${step}`);
      return;
    }

    navigate(-1);
  };

  if (error) return <div className="survey-error">{error}</div>;

  const stepTitle = getStepTitle(uiStep);
  const stepDescription = getStepDescription(uiStep);
  const primaryButtonLabel = getPrimaryButtonLabel(uiStep);
  const trustMessage = getTrustMessage(uiStep);

  return (
    <div className="page">
      <Header hideLatestReportBadge={uiStep === "lead"} />

      <main className="container">
        <div className="survey-page">

          {/* 🔥 WRAPPER ADDED (NON-DESTRUCTIVE) */}
          <div className="survey-layout">

            {/* EXISTING CONTENT WRAPPED */}
            <div className="survey-main">
              <section className="survey-card">
                {loading && (
                  <div className="survey-overlay">
                    <div className="survey-spinner" />
                  </div>
                )}

                {(stepTitle || stepDescription) && (
                  <div className="survey-card-header">
                    {stepTitle && (
                      <div className="survey-card-title-row">
                        {uiStep === "upload" && (
                          <img
                            className="survey-card-title-icon"
                            src="/assets/jim-photo.png"
                            alt=""
                          />
                        )}
                        <h1 className="survey-card-title">{stepTitle}</h1>
                      </div>
                    )}
                    {stepDescription && (
                      <p className="survey-card-description">{stepDescription}</p>
                    )}
                  </div>
                )}

                <div className="survey-content lead-form-wrapper">
                  {renderScreen && (
                    <QuestionRenderer
                      key={renderScreen.id}
                      screen={renderScreen}
                      value={answer}
                      onChange={handleAnswerChange}
                      disabled={loading || submitting}
                      frozen={submitting}
                      uploadContext={{
                        surveyId,
                        customerId,
                        questionId: question?.questionId,
                      }}
                      onUploadRequest={handlePhotoUploadRequest}
                      onUploadSuccess={handlePhotoUploadSuccess}
                      onUploadStart={() => setLoading(true)}
                      onUploadComplete={() => setLoading(false)}
                    />
                  )}

                  {validationError && (
                    <div className="survey-validation-error">
                      {validationError}
                    </div>
                  )}

                  <div className="survey-actions">
                    {uiStep !== "lead" && (
                      <button
                        className="survey-back-button"
                        disabled={loading || submitting}
                        onClick={handleBack}
                      >
                        Back
                      </button>
                    )}

                    <button
                      className="survey-next-button"
                      disabled={loading || submitting}
                      onClick={handleNext}
                    >
                      {primaryButtonLabel}
                    </button>
                  </div>

                  {trustMessage && (
                    <p className="survey-trust-copy">{trustMessage}</p>
                  )}
                </div>
              </section>

              {uiStep !== "lead" ? (
                <div className="mobile-featured">
                  <FeaturedCTA featured={featured} categoryKey={slug} navigate={navigate} />
                </div>
              ) : null}
            </div>

            {/* 🔥 DESKTOP CTA (RIGHT) */}
            {uiStep !== "lead" ? (
              <div className="survey-sidebar">
                <FeaturedCTA featured={featured} navigate={navigate} />
              </div>
            ) : null}

          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
}
